import React, { useEffect, useState } from 'react';
import { DiscardSelection } from '@/assets/icons/circles';
import {
  Checkbox,
  Avatar,
  Box,
  Flex,
  Icon,
  // Link as ChakraLink,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useFlexLayout, useTable } from 'react-table';
import { ViewMorePopover } from '@/components/ats';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { LinkExternal01 } from '@kula-ai/icons/outline/LinkExternal01';
import { KText } from '@/ui-library/components/text';

export const ErrorTable = ({ selected = [] }) => {
  const columns = React.useMemo(
    () => [
      {
        Header: () => {
          return (
            <KText
              size="sm-semi-bold"
              color="secondary-900"
              textTransform="Capitalize"
            >
              Name
            </KText>
          );
        },
        accessor: 'contact.name',
        width: 350,
        Cell: ({ row }) => {
          return (
            <Link
              to={`/candidates/${row?.original?.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Flex alignItems="center" position="relative">
                <Avatar
                  borderRadius="8px"
                  className="avatar-md"
                  src={row?.original?.image_url}
                  width="36px"
                  height="36px"
                  mr="10px"
                  name={row?.original?.name}
                  loading="lazy"
                />
                <Flex flexDirection="column">
                  <KText
                    variant="heading_3"
                    textTransform="capitalize"
                    isTruncated
                    w="180px"
                    enableTooltip={true}
                  >
                    {row?.original?.name}
                  </KText>

                  <KText isTruncated w="180px" enableTooltip={true}>
                    {row?.original?.email}
                  </KText>
                </Flex>
              </Flex>
            </Link>
          );
        },
      },
    ],
    [selected] // eslint-disable-line
  );

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
    useTable(
      {
        columns,
        data: selected || [],
      },
      useFlexLayout
    );
  return (
    <>
      <Box position="sticky" background="white" top="0px" zIndex="1">
        <Table>
          <Thead>
            {headerGroups.map((headerGroup, headerGroupIndex) => (
              <Tr
                key={headerGroupIndex}
                {...headerGroup.getHeaderGroupProps()}
                pos="sticky"
                top="0px "
                zIndex="1"
                minH="60px"
                alignItems="center"
                color="secondary-25"
                borderBottom="1px solid"
                borderBottomColor="border-color"
              >
                {headerGroup.headers.map((column) => {
                  return (
                    <Th
                      key={column.id}
                      {...column.getHeaderProps()}
                      borderBottom="none"
                    >
                      {column.render('Header')}
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
        </Table>
      </Box>
      <Box>
        <Table {...getTableProps()}>
          <Tbody {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row);
              return (
                <Tr
                  key={row.id}
                  borderBottomWidth={rows.length - 1 === i ? 0 : '1px'}
                  borderColor="light.border"
                  {...row.getRowProps()}
                  alignItems="center"
                >
                  {row.cells.map((cell) => {
                    return (
                      <Td
                        key={cell.column.id}
                        borderWidth="0"
                        {...cell.getCellProps()}
                      >
                        {cell.render('Cell')}
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};

export const ErrorTableWithReason = ({
  selected = [],
  isUnsubscribed = false,
  handleSelectedIds = () => {},
}) => {
  const [selectedIds, setSelectedIds] = useState(selected.map(({ id }) => id));

  useEffect(() => {
    handleSelectedIds(selectedIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds]);

  const handleCheckboxChange = (id) => {
    setSelectedIds((prevIds) =>
      prevIds.includes(id)
        ? prevIds.filter((item) => item !== id)
        : [...prevIds, id]
    );
  };

  const handleSelectAllChange = (isChecked) => {
    if (isChecked) {
      setSelectedIds(selected.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const columns = React.useMemo(
    () => [
      ...(isUnsubscribed
        ? [
            {
              Header: () => (
                <Checkbox
                  isChecked={
                    selectedIds.length === selected.length &&
                    selected.length > 0
                  }
                  isIndeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < selected.length
                  }
                  onChange={(e) => handleSelectAllChange(e.target.checked)}
                />
              ),
              accessor: 'contact.select',
              width: 50,
              Cell: ({ row }) => (
                <Checkbox
                  isChecked={selectedIds.includes(row?.original?.id)}
                  onChange={() => handleCheckboxChange(row?.original?.id)}
                />
              ),
            },
          ]
        : []),
      {
        Header: () => (
          <KText
            color="secondary-900"
            size="sm-semi-bold"
            textTransform="Capitalize"
          >
            Name
          </KText>
        ),
        accessor: 'contact.name',
        width: 250,
        Cell: ({ row }) => (
          <Link
            to={`/candidates/${row?.original?.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Flex
              alignItems="center"
              position="relative"
              title={row?.original?.name}
            >
              <Avatar
                borderRadius="8px"
                className="avatar-md"
                src={row?.original?.avatar}
                width="36px"
                height="36px"
                mr="10px"
                name={row?.original?.name}
                loading="lazy"
              />
              <Flex flexDirection="column">
                <KText
                  variant="heading_3"
                  textTransform="capitalize"
                  isTruncated
                  enableTooltip={true}
                  width="220px"
                >
                  {row?.original?.name}
                </KText>
              </Flex>
            </Flex>
          </Link>
        ),
      },
      ...(isUnsubscribed
        ? [
            {
              Header: () => (
                <KText
                  size="sm-semi-bold"
                  color="secondary-900"
                  textTransform="Capitalize"
                >
                  Unsubscribed flows
                </KText>
              ),
              accessor: 'contact.outreaches',
              width: 350,
              Cell: ({ row }) => (
                <ViewMorePopover
                  list={row?.original?.outreaches}
                  type="project"
                  renderInitial={(list) =>
                    list.map((i, ind) => (
                      <Link key={ind} to={`/flows/${i?.id}`}>
                        <Flex gap="5px" alignItems="center">
                          <TruncatedTextWithTooltip
                            key={ind}
                            maxW={'160px'}
                            size="sm-medium"
                            toolTipVariant="default"
                            textTransform="capitalize"
                            text={i?.title}
                            color="ats-primary"
                            _hover={{ textDecoration: 'underline' }}
                            cursor="pointer"
                          />
                          <Icon color="ats-primary" as={LinkExternal01} />
                        </Flex>
                      </Link>
                    ))
                  }
                  renderOthers={() => (
                    <Flex flexDir="column" gap="15px">
                      {row?.original?.outreaches?.map((i, ind) => (
                        <Link key={ind} to={`/flows/${i?.id}`}>
                          <Flex alignItems="center" gap="5px">
                            <KText
                              key={i?.id}
                              fontSize="sm"
                              color="ats-primary"
                              cursor="pointer"
                              _hover={{ textDecoration: 'underline' }}
                            >
                              {i?.title}
                            </KText>
                            <Icon
                              mr="5px"
                              color="ats-primary"
                              as={LinkExternal01}
                            />
                          </Flex>
                        </Link>
                      ))}
                    </Flex>
                  )}
                  popOverStyles={{
                    maxW: '20em',
                  }}
                  moreTextStyles={{
                    size: 'sm',
                    color: 'ats-text-secondary',
                    _hover: { color: 'ats-primary' },
                  }}
                />
              ),
            },
          ]
        : [
            {
              Header: () => (
                <KText
                  size="sm-semi-bold"
                  color="secondary-700"
                  textTransform="Capitalize"
                >
                  Reason
                </KText>
              ),
              accessor: 'contact.message',
              width: 350,
              Cell: ({ row }) => (
                <Flex alignItems="center" position="relative">
                  <Flex flexDirection="column">
                    <KText
                      enableTooltip={true}
                      textTransform="capitalize"
                      isTruncated
                    >
                      {row?.original?.message}
                    </KText>
                  </Flex>
                </Flex>
              ),
            },
          ]),
    ],
    [selectedIds, isUnsubscribed] // eslint-disable-line
  );

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
    useTable(
      {
        columns,
        data: selected || [],
      },
      useFlexLayout
    );

  return (
    <Table {...getTableProps()}>
      <Thead>
        {headerGroups.map((headerGroup, headerGroupIndex) => (
          <Tr
            key={headerGroupIndex}
            {...headerGroup.getHeaderGroupProps()}
            pos="sticky"
            top="0px"
            zIndex="1"
            minH="60px"
            alignItems="center"
            bg="secondary-25"
            borderBottom="1px solid"
            borderBottomColor="border-color"
            sx={{
              '> th:nth-of-type(2)': {
                borderColor: 'border-color',
                borderLeftWidth: '1px',
              },
            }}
          >
            {headerGroup.headers.map((column) => (
              <Th
                key={column.id}
                {...column.getHeaderProps()}
                borderBottom="none"
              >
                {column.render('Header')}
              </Th>
            ))}
          </Tr>
        ))}
      </Thead>
      <Tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);
          return (
            <Tr key={row.id} {...row.getRowProps()}>
              {row.cells.map((cell) => (
                <Td
                  key={cell.column.id}
                  {...cell.getCellProps()}
                  display="flex"
                  alignItems="center"
                >
                  {cell.render('Cell')}
                </Td>
              ))}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
};

export const DeleteErrors = ({
  isOpen = false,
  onClose = () => {},
  onCloseParent = () => {},
  errors = [],
}) => {
  // const message = errors?.every((error) => error?.message === 'already_sent')
  //   ? 'The Flow is already in progress. You cannot remove a candidate from a running Flow.'
  //   : 'Either the Flow is already in progress or the candidate has already been removed from the Flow.';
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        onCloseParent();
      }}
      size="lg"
      isCentered
    >
      <ModalOverlay />
      <ModalContent p="8px" pos="fixed">
        <ModalCloseButton
          onClick={() => {
            onClose();
            onCloseParent();
          }}
        />
        <Flex
          mt="30px"
          mb="20px"
          alignItems="center"
          flexDirection="column"
          gap="12px"
          data-test-id="flow-remove-error-modal"
        >
          <Icon as={DiscardSelection} w="140px" h="122px" />
          <KText color="secondary-900" size="xl-semi-bold">
            Candidate couldn’t be removed from the Flow.
          </KText>
          {errors?.every((error) => error?.message === 'not_found') ? (
            <KText color="#696B6C" w="450px" textAlign="center">
              The candidates has been already removed from the Flow.
            </KText>
          ) : (
            <></>
            // <KText color="#696B6C" w="450px" textAlign="center">
            //   {`${message} `}
            //   You can stop the Flow if you want to stop any further messages.
            //   Need help?
            //   <ChakraLink
            //     href="https://help.kula.ai/en/articles/7234928-stop-a-flow-for-a-candidate"
            //     ml="6px"
            //     style={{
            //       color: '#0043CE',
            //       textDecoration: 'underline',
            //     }}
            //     target="_blank"
            //   >
            //     How to stop a Flow
            //   </ChakraLink>
            // </KText>
          )}
        </Flex>
        <Box
          overflow="scroll"
          maxH="300px"
          m="12px 24px"
          borderRadius="md"
          border="1px solid"
          borderColor="border-color"
        >
          <ErrorTable selected={errors} />
        </Box>
      </ModalContent>
    </Modal>
  );
};
